import { IdentifiableDiagnostic } from "../validation-service";
import { AxiosError, AxiosResponse } from "axios";
import { ErrorHandler } from "../error-handler";
import { DiagnosticSeverity, ValidatorService } from "../validation-service";
import { AbortRequestEventBus, MessageEventBus } from "../../../event-bus";
import { curriedDelete, curriedGet, curriedPost, curriedPut } from "../abort-controller";
import { DirtyState } from "../dirty-state";
import { ModelData } from "../statecharts/ModelData";
import RappidContext from "../../rappid/rappid-context";
import { StatechartsService } from "../statecharts/statecharts-service";
import { waitUntilCleanState } from "../storage/utils";

export class PlayerService {
  constructor(
    private baseURL: string,
    private validatorService: ValidatorService,
    private context: RappidContext,
    private cloudStatechartService: StatechartsService,
    private waitForDirtyState = true,
    private dirtyState?: DirtyState
  ) {}

  private publishingAborted = false;
  private loadingLoopDelay = 0;
  private loadingTimeout: NodeJS.Timeout;
  private loadingLoopTimeout: NodeJS.Timeout;
  private errorHandler = new ErrorHandler("Player Service");
  private mode: "update" | "publish" | "delete" | "getInfo";

  private config() {
    return {
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
      params: { type: "player" },
    };
  }

  protected handleError(
    res: AxiosError | null,
    text?: string,
    showError = true,
    abortMessage?: string,
    showAbort = false
  ) {
    return this.errorHandler.handle(res, "player-service", text, showError, abortMessage, showAbort);
  }

  private waitForPublishUrl(playerId: string): string {
    return `/api/models/${playerId}/simulation`;
  }

  private publishUrl(modelId: string) {
    return `/api/models/${modelId}/player`;
  }

  private updateUrl(modelId: string, playerId: string) {
    return `/api/models/${modelId}/player/${playerId}`;
  }

  private deleteUrl(modelId: string, playerId: string) {
    return this.updateUrl(modelId, playerId);
  }

  private getAbortMessage() {
    let msg: string;

    switch (this.mode) {
      case "publish":
        msg = "Publishing of Player";
        break;
      case "update":
        msg = "Updating of Player";
        break;
      case "delete":
        msg = "Deletion of Player";
        break;
      case "getInfo":
        msg = "Loading of Player Info";
    }
    msg += " got manually aborted.";
    return msg;
  }

  private getActionErrorMessage() {
    let msg = "Player cannot be ";
    switch (this.mode) {
      case "publish":
        msg += "published";
        break;
      case "update":
        msg += "updated";
        break;
      case "delete":
        msg += "deleted";
        break;
      case "getInfo":
        msg = "Player Info cannot be loaded";
    }
    return msg;
  }

  private stopAbortHandler = () => {
    this.abort(this.getAbortMessage());
  };

  async getModelMetadata(modelId: string): Promise<ModelData> {
    this.mode = "getInfo";
    return await this.cloudStatechartService.getModelMetadata(modelId, this.waitForDirtyState).catch((err) => {
      throw this.abort(err);
    });
  }

  async checkDirtyState() {
    if (this.waitForDirtyState && this.dirtyState?.isDirty()) {
      await waitUntilCleanState(this.dirtyState).catch(() => {
        throw this.handleError(
          undefined,
          `${this.getActionErrorMessage()} as model saving timed out.`,
          true,
          undefined,
          false
        );
      });
    }
  }

  async delete(modelId: string, playerId: string) {
    this.mode = "delete";

    AbortRequestEventBus.on("abort", this.stopAbortHandler);

    await this.checkDirtyState();

    const requestPromise = curriedDelete(this.deleteUrl(modelId, playerId), this.config());

    return await requestPromise
      .then(() => {
        AbortRequestEventBus.off("abort", this.stopAbortHandler);
        return;
      })
      .catch((err: AxiosError) => {
        AbortRequestEventBus.off("abort", this.stopAbortHandler);
        throw this.abort(err);
      });
  }

  async publishOrUpdate(modelId: string, modelJson: string, mode: "update" | "publish", playerId?: string) {
    if (mode === "update" && !playerId) {
      const errMsg = "PlayerId is required to update player.";
      MessageEventBus.emit("error", {
        id: "player-service",
        text: errMsg,
      });
      throw errMsg;
    } else {
      this.mode = mode;
    }
    this.publishingAborted = false;

    AbortRequestEventBus.on("abort", this.stopAbortHandler);

    await this.checkDirtyState();

    // TODO why do I need this before validate? #874
    await this.cloudStatechartService.createOrUpdate(modelId, modelJson, this.context);
    const diagnostics = await this.validatorService.validateModelById(modelId);
    let errors: IdentifiableDiagnostic[];
    if (diagnostics) {
      errors = diagnostics.filter((d) => d.severity === DiagnosticSeverity.ERROR);
    }
    AbortRequestEventBus.off("abort", this.stopAbortHandler);

    if (errors?.length > 0) {
      const errMsg = `${this.getActionErrorMessage()} due to errors in the model.`;
      MessageEventBus.emit("error", {
        id: "player-service",
        text: errMsg,
      });
      throw errMsg;
    }

    let triggerPublishRequestPromise: Promise<AxiosResponse>;
    if (mode === "publish") {
      triggerPublishRequestPromise = curriedPost(this.publishUrl(modelId), this.config());
    } else {
      triggerPublishRequestPromise = curriedPut(this.updateUrl(modelId, playerId), this.config());
    }

    await triggerPublishRequestPromise
      .then((res) => {
        if (mode === "publish") {
          playerId = res.data;
        }
      })
      .catch((err: AxiosError) => {
        throw this.abort(err);
      });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: (playerId: string) => void, reject: (reason?: any) => void) => {
      // use recursive setTimeout with loadingLoop instead of setInterval to make sure, that next function call is only started after last one was finished
      this.startLoadingLoop(resolve, reject, playerId);

      this.loadingTimeout = setTimeout(
        () => {
          reject(this.abort(`${this.getActionErrorMessage()}. Timeout reached.`));
        },
        1000 * 60 * 3
      );
    });
  }

  private abort(err: AxiosError | string) {
    if (this.mode === "publish" || this.mode === "update") this.publishingAborted = true;
    this.cleanTimeouts();

    if (typeof err !== "string") {
      return this.handleError(err, `${this.getActionErrorMessage()} due to an unknown error.`, true, undefined, false);
    } else {
      return err;
    }
  }

  private cleanTimeouts() {
    if (this.loadingLoopTimeout) clearTimeout(this.loadingLoopTimeout);
    if (this.loadingTimeout) clearTimeout(this.loadingTimeout);
    AbortRequestEventBus.off("abort", this.stopAbortHandler);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private startLoadingLoop(resolve: (playerId: string) => void, reject: (reason?: any) => void, playerId: string) {
    // start immediately with the first request
    this.loadingLoopDelay = 0;
    this.loadingLoop(resolve, reject, playerId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private loadingLoop(resolve: (playerId: string) => void, reject: (reason?: any) => void, playerId: string) {
    if (!this.publishingAborted) {
      this.loadingLoopTimeout = setTimeout(async () => {
        const waitForPublishRequestPromise = curriedGet(this.waitForPublishUrl(playerId), this.config());

        await waitForPublishRequestPromise
          .then((response) => {
            if (response.status === 201) {
              clearTimeout(this.loadingTimeout);
              AbortRequestEventBus.off("abort", this.stopAbortHandler);
              resolve(playerId);
              return;
            }
            // start with the next request only after 1s
            this.loadingLoopDelay = 1000;
            this.loadingLoop(resolve, reject, playerId);
          })
          .catch((e: AxiosError) => {
            reject(this.abort(e));
          });
      }, this.loadingLoopDelay);
    }
  }
}
